import { createId } from '@/lib/carwash/ids'

/*
 * Vehicle and staff photos are kept in the browser's IndexedDB, which holds
 * far more than localStorage. Records only store a short reference such as
 * "local:<id>". Regular URLs (for example the demo photos) pass through as
 * they are. When IndexedDB is unavailable the photo itself is returned so it
 * can still be saved with the record.
 */

const DB_NAME = 'washline'
const DB_VERSION = 1
const STORE = 'photos'
const LOCAL_PREFIX = 'local:'

const cache = new Map<string, string>()
let dbPromise: Promise<IDBDatabase> | null = null

const openDb = (): Promise<IDBDatabase> => {
    if (dbPromise) return dbPromise

    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('Photo storage is not available in this browser.'))
            return
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION)

        request.onupgradeneeded = () => {
            const db = request.result
            if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
        }

        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error ?? new Error('Photo storage could not be opened.'))
    })

    dbPromise.catch(() => {
        dbPromise = null
    })

    return dbPromise
}

const runRequest = async <T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest): Promise<T> => {
    const db = await openDb()

    return new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode)
        const request = action(transaction.objectStore(STORE))

        request.onsuccess = () => resolve(request.result as T)
        request.onerror = () => reject(request.error ?? new Error('Photo storage request failed.'))
    })
}

export const isLocalPhoto = (ref: string | null | undefined): boolean => Boolean(ref && ref.startsWith(LOCAL_PREFIX))

export const getCachedPhoto = (ref: string | null | undefined): string | null => {
    if (!ref) return null
    if (!isLocalPhoto(ref)) return ref
    return cache.get(ref) ?? null
}

export const savePhoto = async (dataUrl: string): Promise<string> => {
    const id = createId()
    const ref = `${LOCAL_PREFIX}${id}`

    try {
        await runRequest('readwrite', store => store.put(dataUrl, id))
        cache.set(ref, dataUrl)
        return ref
    }
    catch {
        return dataUrl
    }
}

export const resolvePhoto = async (ref: string | null | undefined): Promise<string | null> => {
    if (!ref) return null
    if (!isLocalPhoto(ref)) return ref

    const cached = cache.get(ref)
    if (cached) return cached

    const value = await runRequest<unknown>('readonly', store => store.get(ref.slice(LOCAL_PREFIX.length)))

    if (typeof value !== 'string') return null

    cache.set(ref, value)
    return value
}

export const deletePhoto = async (ref: string | null | undefined): Promise<void> => {
    if (!ref || !isLocalPhoto(ref)) return

    cache.delete(ref)

    try {
        await runRequest('readwrite', store => store.delete(ref.slice(LOCAL_PREFIX.length)))
    }
    catch {
        // Nothing to clean up when photo storage is unavailable.
    }
}

export const clearPhotos = async (): Promise<void> => {
    cache.clear()

    try {
        await runRequest('readwrite', store => store.clear())
    }
    catch {
        // Nothing to clear when photo storage is unavailable.
    }
}
